import { AtmosphericDataEnum } from "@bienesis/common";
import { useTranslation } from "react-i18next";

import { BottomSheetStackApi } from "@/components/BottomSheet";
import { AreaStateAggregateType } from "@/routes/area-aggregate/types/area-state-aggregate.type";
import { ModuleCommandEnum } from "@/types/enums/module-command.enum";

import { SheetsEnum } from "../enums/sheets.enum";
import { SheetsType } from "../types/sheets.type";
import { SwitchSheetsTitlesResult } from "../types/switch-sheets-titles.type";

export function switchSheetsTitles({
  areaStateAggregate,
  areaName,
  sheet,
}: {
  /** Optionnel — pas requis pour les sheets niveau application
   *  (ex. `SCAN_MODULE`). */
  areaStateAggregate?: AreaStateAggregateType;
  /** Optionnel — nom de la parcelle, utilisé comme titre pour `CLIMATE_VIEW`. */
  areaName?: string;
  sheet: BottomSheetStackApi<SheetsType>;
}): SwitchSheetsTitlesResult {
  const { t: translator } = useTranslation();
  switch (sheet.visibleView?.id) {
    case SheetsEnum.SCAN_MODULE:
      return { hasTitle: true, title: translator("quickModuleAccess.title") };
    case SheetsEnum.MODULES_LIST:
      return { hasTitle: true, title: translator("area.detailsModules") };
    case SheetsEnum.MODULE_ACCESS:
      return {
        hasTitle: true,
        title: `${translator("canopee")} ${sheet.visibleView.moduleAddress}`,
      };
    case SheetsEnum.AREA_LOCK:
      if (!areaStateAggregate) return { hasTitle: false };
      return areaStateAggregate.isLocked
        ? { hasTitle: true, title: translator("area.endOfMaintenance") }
        : { hasTitle: true, title: translator("area.settingOutOfOrder") };
    case SheetsEnum.ACTION:
      return sheet.visibleView.action === ModuleCommandEnum.UNFOLD
        ? {
            hasTitle: true,
            title: translator("modulesActions.confirmation.titleUnfold"),
          }
        : {
            hasTitle: true,
            title: translator("modulesActions.confirmation.titleFold"),
          };
    case SheetsEnum.ACTION_IN_PROGRESS:
      return { hasTitle: false };
    case SheetsEnum.EMERGENCY_STOP:
      return { hasTitle: true, title: translator("action.emergencyStop") };
    case SheetsEnum.SKIP_ACTION:
      return {
        hasTitle: true,
        title:
          sheet.visibleView.context.skippedCommand === ModuleCommandEnum.UP
            ? translator("modulesActions.confirmation.skippingUp")
            : translator("modulesActions.confirmation.skippingClose"),
      };
    case SheetsEnum.CLIMATE_RAINFALL_VIEW:
      return {
        hasTitle: true,
        title: `${areaName} - ${translator("meteo.climateHistory.rainfall")}`,
      };
    case SheetsEnum.CLIMATE_ATMOSPHERIC_VIEW: {
      const atmosphericLabelKey = {
        [AtmosphericDataEnum.TEMPERATURE]: "meteo.climateHistory.temperatures",
        [AtmosphericDataEnum.IRRADIANCE]:
          "meteo.climateHistory.solarIrradiance",
        [AtmosphericDataEnum.HUMIDITY]: "meteo.climateHistory.humidity",
        [AtmosphericDataEnum.WIND]: "meteo.climateHistory.wind",
      }[sheet.visibleView.type];
      return {
        hasTitle: true,
        title: `${areaName} - ${translator(atmosphericLabelKey as never)}`,
      };
    }
    case SheetsEnum.CLIMATE_HISTORY_CHOICE:
      return {
        hasTitle: true,
        title: translator("meteo.climateHistory.title"),
      };
    case SheetsEnum.WEATHER_FORECAST:
      return {
        hasTitle: true,
        title: areaName
          ? `${areaName} - ${translator("meteo.forecast.title")}`
          : translator("meteo.forecast.title"),
      };
    case SheetsEnum.PROTECTION_LIST:
      return { hasTitle: true, title: translator("meteo.activeProtections") };
    case SheetsEnum.PROTECTION_DETAIL: {
      const name = translator(
        `protection.${sheet.visibleView.protectionKey}`
      ).toLocaleLowerCase();
      return {
        hasTitle: true,
        title: translator("protection.activeTitle", {
          name,
        }),
      };
    }
    default:
      return { hasTitle: false };
  }
}
